/**
 * Mark that a live SPA session exists.
 *
 * This used to read `document.cookie`, take whichever cookie happened to be first,
 * cut it at the last "%", and store the result under `localStorage.token` — which
 * `api.ts` then sent as a Bearer credential. That value was never a usable token:
 * the session cookie is URL-encoded, so the truncation mangled it, and cookie order
 * is not defined, so it was often the XSRF cookie instead. SPA requests authenticate
 * through the Sanctum session cookie; the Bearer header was decorative.
 *
 * It also forced `session.http_only` to false, because the frontend had to be able to
 * read the session cookie from JavaScript. Removing the scrape is what lets that
 * cookie go back to being HttpOnly and out of reach of injected script.
 *
 * `localStorage.token` stays as the session marker that `offline/session.ts` gates on
 * (together with `user`), but it now holds a non-secret constant. A real Sanctum token
 * — impersonation, or a mobile client — is left exactly as it is.
 */
export const SESSION_MARKER = "session";

/** A Sanctum personal access token looks like "123|abcdef...". */
const SANCTUM_TOKEN = /^\d+\|[A-Za-z0-9]+$/;

export const isRealToken = (value: string | null): boolean =>
  !!value && SANCTUM_TOKEN.test(value);

export const updateToken = () => {
  try {
    const existing = localStorage.getItem("token");

    // Never overwrite a genuine credential (impersonation stores one here).
    if (isRealToken(existing)) {
      return;
    }

    localStorage.setItem("token", SESSION_MARKER);
  } catch {
    // localStorage throws in locked-down/private contexts; a missing marker only
    // means the offline gate stays closed, which is the safe direction.
  }
};
